import { createContext, useContext } from "react";
import type { LibraryMode, Tier } from "@/lib/tier";

export type Workspace = {
  id: string;
  owner_user_id: string;
  display_name: string | null;
  workspace_label: string | null;
  default_library_category_id: string | null;
  is_primary: boolean;
  account_tier: Tier;
  sort_order: number;
  library_isolated: boolean;
  brand_handle: string | null;
  brand_handle_changed_at: string | null;
  /** Per-workspace reading font override. NULL = inherit user_preferences.font_preference. */
  workspace_font_preference: 'sans' | 'serif' | null;
  /** When false, the Composer's Codex dock tab + its Framework/Voice/Avatars sub-tabs are hidden. Default true. */
  codex_enabled: boolean;
  /** For non-primary workspaces, the Persona (primary profile) they belong to. NULL for primaries. */
  parent_profile_id: string | null;
};

export type WsCtx = {
  /** Workspaces scoped to the active Persona (the Persona itself + its child workspaces). Use this for tabs, counts, and create gates. */
  workspaces: Workspace[];
  /** Every profile row owned by the auth user across ALL Personas. Use this for the Persona switcher and Super Admin tools. */
  allOwnedWorkspaces: Workspace[];
  active: Workspace | null;
  /** The Persona (primary profile) the active workspace belongs to. */
  activePersonaId: string | null;
  setActive: (id: string) => void;
  refresh: () => Promise<void>;
  loading: boolean;
  /** Resolved workspace cap accounting for tier base + purchased add-ons. `null` = unlimited. */
  workspaceLimit: number | null;
  /** True when the user can create another workspace under their current plan + add-ons. */
  canCreateWorkspace: boolean;
  /** True when usage caps / creation gates should be bypassed (super-admin acting on own account). */
  superAdminBypassCaps: boolean;
  /** Account tier of the primary (owned) workspace — drives all tier-sensitive features. Honors super-admin "view as" override. */
  primaryTier: Tier | null;
  /** Real account tier of the primary workspace, ignoring any super-admin "view as" override. */
  realPrimaryTier: Tier | null;
  /** Resolved library mode for the active workspace (drives chips/move/share UI). */
  libraryMode: LibraryMode;
};

export const WorkspaceContext = createContext<WsCtx>({
  workspaces: [], allOwnedWorkspaces: [], active: null, activePersonaId: null, setActive: () => {}, refresh: async () => {}, loading: true,
  workspaceLimit: 1, canCreateWorkspace: false, superAdminBypassCaps: false, primaryTier: null, realPrimaryTier: null, libraryMode: "single",
});

export const WORKSPACE_STORAGE_KEY = "excerptus.activeWorkspaceId";

export const useWorkspace = () => useContext(WorkspaceContext);
